import { useState } from "react";

import clsx from "clsx";
import { orderBy, take } from "lodash";

import {
  DataTypeProvider,
  IntegratedSelection,
  SelectionState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  TableColumnResizing,
  TableFixedColumns,
  TableHeaderRow,
  TableSelection,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { useProductAnalyticsContext } from "@features/product-analytics/hooks";

import { filterDataByColumnAndOperation } from "../../metricsData";
import ProductItemRelationModal from "../product-item-relation-modal";

import { TableCell, TableRow } from "./body-components";
import {
  NAME_COLUMN_ID,
  ROW_HEIGHT,
  SELECTION_COLUMN_WIDTH,
} from "./constants";
import {
  CacPercentage,
  Currency,
  EfficiencyIndex,
  Multiplier,
  NameDataFormatter,
  Number,
} from "./data-type-formatters";
import { ASPClickThrough } from "./data-type-formatters/asp-url-clickthrough";
import {
  TableHeaderCell,
  TableHeaderContent,
  TableHeadWithFixedRow,
  TableSelectionCell,
  TableSelectionHeaderCell,
} from "./header-components";

export const getColumns = (startingColumnTitle: string, tabView: string) => [
  { name: "name", title: startingColumnTitle },
  { name: "rev", title: "Revenue" },
  { name: "rev_first_time", title: "Rev (1st Time)" },
  { name: "rev_returning", title: "Rev (Returning)" },
  { name: "txns", title: "Orders" },
  { name: "quantity", title: "Units Sold" },
  { name: "cacPercentage", title: "CAC index" },
  { name: "roasPercentage", title: "Roas Index" },
  { name: "proportional_spend", title: "Product Spend" },
  { name: "proas", title: "Product ROAS" },
  { name: "pcac", title: "Product CAC" },
  { name: "aov", title: "AOV" },
  ...(tabView === "product" ? [{ name: "asp", title: "ASP" }] : []), // Conditionally include "asp"
  { name: "itemSoldRatio", title: "UPO" },
];

export default function TabTable({
  startingColumnTitle,
  selectedItems,
  setSelectedItems,
  metricRows,
}: {
  startingColumnTitle: string;
  setSelectedItems: (ids: string[]) => void;
  selectedItems: string[];
  metricRows: any[];
}) {
  const {
    tableState: { sorting, metricFilters },
    setMetricFilters,
    selectedQuickSelect,
    setSelectedProducts,
    setQuickSelect,
    chartSelected,
    productItemRelationModalOpen,
    setProductItemRelationModalOpen,
    selectedRelationRow,
    tabState: { tabView },
  } = useProductAnalyticsContext();

  const [defaultColumnWidths] = useState([
    { columnName: "name", width: 300 },
    { columnName: "rev", width: 150 },
    { columnName: "rev_first_time", width: 150 },
    { columnName: "rev_returning", width: 150 },
    { columnName: "txns", width: 100 },
    { columnName: "quantity", width: 130 },
    { columnName: "cacPercentage", width: 120 },
    { columnName: "roasPercentage", width: 120 },
    { columnName: "proportional_spend", width: 150 },
    { columnName: "proas", width: 150 },
    { columnName: "pcac", width: 150 },
    { columnName: "aov", width: 120 },
    { columnName: "asp", width: 120 },
    ...(tabView === "product" ? [{ columnName: "asp", width: 120 }] : []), // Conditionally include "asp"
    { columnName: "itemSoldRatio", width: 120 },
  ]);

  const handleSelectionChange = (newSelection: (string | number)[]) => {
    setSelectedItems(newSelection as any);
    setQuickSelect("");
  };
  let filteredRows = metricRows;

  if (metricFilters.length !== 0) {
    filteredRows = metricFilters.reduce((inProgressFilteredRows, filter) => {
      return filterDataByColumnAndOperation(
        filter.value,
        inProgressFilteredRows,
        filter.operator,
        filter.id,
      );
    }, metricRows);
  }
  if (chartSelected.length > 0) {
    filteredRows = filteredRows.filter((row) => chartSelected.includes(row.id));
  }

  const filteredAndSortedRows = orderBy(
    filteredRows,
    [sorting.id],
    [sorting.order],
  ).map((row, index) => {
    return {
      ...row,
      index,
    };
  });

  if (selectedQuickSelect?.quickSelect?.length > 0 && tabView === "product") {
    setSelectedProducts(take(filteredAndSortedRows, 10).map((row) => row.id));
  }

  return (
    <div
      className={clsx(
        "sales-table-v3 sales-table-v3-compact-row-height h-[35rem]",
        "border border-v2-secondary bg-v2-primary rounded-lg overflow-hidden relative text-xs",
      )}
    >
      <Grid
        rows={filteredAndSortedRows}
        columns={getColumns(startingColumnTitle, tabView)}
        getRowId={(row) => row.id}
      >
        <SelectionState
          selection={selectedItems}
          onSelectionChange={handleSelectionChange}
        />
        <IntegratedSelection />
        <VirtualTable
          height="auto"
          estimatedRowHeight={ROW_HEIGHT}
          headComponent={TableHeadWithFixedRow as React.ComponentType<object>}
          rowComponent={(props) => <TableRow {...props}></TableRow>}
          cellComponent={TableCell}
          columnExtensions={defaultColumnWidths}
          noDataCellComponent={() => (
            <td
              colSpan={getColumns(startingColumnTitle, tabView).length}
              className="text-center"
            >
              No data.{" "}
              <button
                className="underline text-primary pt-8"
                onClick={() => setMetricFilters([])}
              >
                Clear {metricFilters.length} filters.{" "}
              </button>
            </td>
          )}
        />
        <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
        <TableHeaderRow
          cellComponent={TableHeaderCell}
          contentComponent={TableHeaderContent}
        />
        <TableSelection
          showSelectAll
          selectionColumnWidth={SELECTION_COLUMN_WIDTH}
          headerCellComponent={TableSelectionHeaderCell}
          cellComponent={TableSelectionCell}
        />
        <DataTypeProvider
          for={[NAME_COLUMN_ID]}
          formatterComponent={NameDataFormatter}
        />
        <DataTypeProvider
          for={["cacPercentage"]}
          formatterComponent={CacPercentage}
        />
        <DataTypeProvider
          for={["roasPercentage"]}
          formatterComponent={EfficiencyIndex}
        />
        <DataTypeProvider
          for={[
            "rev",
            "aov",
            "pcac",
            "proportional_spend",
            "spend",
            "rev_first_time",
            "rev_returning",
          ]}
          formatterComponent={Currency}
        />
        <DataTypeProvider
          for={["txns", "quantity"]}
          formatterComponent={Number}
        />
        <DataTypeProvider for={["asp"]} formatterComponent={ASPClickThrough} />
        <DataTypeProvider
          for={["proas", "itemSoldRatio"]}
          formatterComponent={Multiplier}
        />
        <TableFixedColumns
          leftColumns={[TableSelection.COLUMN_TYPE, NAME_COLUMN_ID]}
        />
      </Grid>
      <ProductItemRelationModal
        item={selectedRelationRow}
        isModalOpen={productItemRelationModalOpen}
        setIsModalOpen={setProductItemRelationModalOpen}
      />
    </div>
  );
}
